// src/pages/Auth/GoogleCallback.jsx
// ✅ FIXED:
// - Saves token + user to localStorage (doesn't overwrite with /me if needs_role)
// - If needs_role → redirect_url points to /auth/select-role
// - Works for both popup (Register) and full-page (Login) flows

import React, { useEffect, useState, useRef } from 'react';
import { Container, Box, CircularProgress, Typography, Alert, Button, Paper } from '@mui/material';
import { authAPI } from '../../services/api';

const GoogleCallback = () => {
  const [status, setStatus] = useState('loading');
  const [error,  setError]  = useState('');
  const [info,   setInfo]   = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    processGoogleCallback();
  }, []);

  const processGoogleCallback = async () => {
    try {
      const params     = new URLSearchParams(window.location.search);
      const code       = params.get('code');
      const errorParam = params.get('error');

      // ── OAuth denied / cancelled ──────────────────────────────
      if (errorParam) {
        setError(params.get('error_description') || decodeURIComponent(errorParam));
        setStatus('error');
        return;
      }

      if (!code) {
        setError('No authorization code received from Google.');
        setStatus('error');
        return;
      }

      setInfo('Verifying with Google...');

      // ── Send code to backend ──────────────────────────────────
      const response = await authAPI.googleProcessCode({ code });
      const data     = response.data;

      if (!data.success) {
        setError(data.message || 'Failed to process authentication');
        setStatus('error');
        return;
      }

      // ── Save token + user WITHOUT calling /me ─────────────────
      // If needs_role=true, user_type is null in the response.
      // We must NOT call refreshUser()/me here because /me might
      // return a stale DB value. Keep the data from the OAuth response.
      if (data.access_token && data.user) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user',  JSON.stringify(data.user));
      }

      setStatus('success');

      // Build redirect URL — ensure it's a relative path for React Router
      let redirectUrl = data.redirect_url || '/buyer/dashboard';
      // Strip domain if present so we navigate within the app
      try {
        const u = new URL(redirectUrl);
        redirectUrl = u.pathname + u.search;
      } catch {
        // already a relative path
      }

      // ── If needs role: always go to select-role ───────────────
      if (data.needs_role || !data.user?.user_type) {
        redirectUrl = '/auth/select-role';
      }

      setInfo(data.needs_role ? 'Account created! Setting up your role...' : 'Login successful! Redirecting...');

      setTimeout(() => {
        if (window.opener && !window.opener.closed) {
          // We're in a popup — send message to parent then close
          window.opener.postMessage(
            { type: 'GOOGLE_AUTH_REDIRECT', redirectUrl },
            window.location.origin
          );
          setTimeout(() => window.close(), 300);
        } else {
          // Normal tab navigation
          window.location.href = redirectUrl;
        }
      }, 800);

    } catch (err) {
      console.error('GoogleCallback error:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to connect to server. Please try again.'
      );
      setStatus('error');
    }
  };

  // ── Loading ────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                   justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
          <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt:3, fontWeight:600 }}>
              Processing Google authentication...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
              Please wait while we complete your login
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // ── Success ────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                   justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
          <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
            <Box sx={{ color:'success.main', mb:3 }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="transparent"/>
                <path d="M7 12L10 15L17 8" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Typography variant="h5" sx={{ mb:2, fontWeight:600 }}>
              Authentication Successful! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb:3 }}>
              {info || 'Google authentication completed successfully.'}
              <br />Redirecting you now...
            </Typography>
            <CircularProgress size={24} sx={{ mt:2 }} />
          </Paper>
        </Box>
      </Container>
    );
  }

  // ── Error ──────────────────────────────────────────────────────
  return (
    <Container maxWidth="sm">
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                 justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
        <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
          <Alert severity="error" sx={{ mb:3, textAlign:'left' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Google Authentication Failed
            </Typography>
            <Typography variant="body2" sx={{ mt:0.5 }}>{error}</Typography>
          </Alert>
          <Box sx={{ display:'flex', gap:2, justifyContent:'center', flexWrap:'wrap' }}>
            <Button variant="contained" onClick={() => window.location.href = '/login'}
              sx={{ minWidth:120 }}>
              Back to Login
            </Button>
            <Button variant="outlined" sx={{ minWidth:120 }}
              onClick={() => { hasRun.current = false; window.location.reload(); }}>
              Try Again
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default GoogleCallback;